import { watch } from '@vue/composition-api';
import { container, singleton, DependencyContainer } from 'tsyringe';
import { Model, createModel, InferModelType, InferStateType } from './models';

interface TokenConfiguration {
  serialize?: boolean;
}

const DefaultTokenConfiguration: TokenConfiguration = {
  serialize: true,
};

@singleton()
export class ModelManager {
  private _stateContainer: DependencyContainer;

  private _configuration = new Map<string, TokenConfiguration>();

  constructor() {
    this._stateContainer = container.createChildContainer();
  }

  get global() {
    return this.get('__global__', 'GlobalModel');
  }

  get<ModelType extends Model, Type extends Model['type']>(
    id: string,
    type: Type,
    defaultState?: InferStateType<ModelType, Type>
  ): InferModelType<ModelType, Type> {
    if (!this._stateContainer.isRegistered(id)) {
      let state = defaultState;

      const serializedAppState = localStorage.getItem('serializedAppState');
      if (serializedAppState && serializedAppState.length > 0) {
        // TODO could be a potential bottleneck, as for each model resolution this is is executed, thinking of a big serializedAppState
        const appState = JSON.parse(serializedAppState);
        if (typeof appState === 'object' && typeof appState[id] === 'object' && appState[id].type === type) {
          state = appState[id].state;
        }
      }

      const instance = createModel(type, state);
      // automatically persist state if it is changed
      if (instance && typeof instance.state !== undefined) {
        watch(
          () => instance.state,
          () => localStorage.setItem('serializedAppState', this.serialize()),
          { deep: true }
        );
      }
      this._stateContainer.register(id, { useValue: instance });
      this.configure(id);
    }

    return this._stateContainer.resolve(id);
  }

  configure(token: string, configuration: TokenConfiguration = DefaultTokenConfiguration) {
    this._configuration.set(token, configuration);
  }

  // This method assumes, that the state attribute of each model *is* serializable (if it exists)
  serialize() {
    // get possibly previous state (of models that haven't been registered yet)
    const appState = JSON.parse(localStorage.getItem('serializedAppState') || '{}');
    const configEntries = Array.from(this._configuration.entries());
    return JSON.stringify(
      configEntries.reduce((acc, e) => {
        const [token, config] = e;
        if (config.serialize) {
          const inst = this._stateContainer.resolve(token) as Model;
          if (inst && typeof inst.state !== undefined) acc[token] = { type: inst.type, state: inst.state };
        }
        return acc;
      }, appState)
    );
  }

  // alternative method based serializtion, but I think the convention that *if* there is a state attribute in a model, it
  // should be serializable is less verbose:
  //
  //   serialize() {
  //     const configEntries = Array.from(this._configuration.entries());
  //     const lastEntryIdx = configEntries.length - 1;
  //     return `{${configEntries
  //       .map((e, i) => {
  //         const [token, config] = e;
  //         if (config.serialize) {
  //           // resolveAll would be better with model type tokens as configurations, but the serialized representation currently only supports one key per token
  //           // TODO this should probably be done completely different:
  //           //   different serialization representation/structure, or different kind of configuration, allowing a cleaner serialization alltogether
  //           // currently the configuration is per instance key/token, so the following is currently sufficient
  //           const inst = this._stateContainer.resolve(token) as Model;
  //           if (inst && typeof inst.serialize === 'function')
  //             return `"${token}":{"type":"${inst.type}","state":${inst.serialize()}}${i === lastEntryIdx ? '' : ','}`;
  //         }
  //         return '';
  //       })
  //       .join('')}}`;
  //   }
}
